import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const ESTIMATED_HEIGHT = 360;
const ESTIMATED_WIDTH = 300;

interface PeriodPopoverProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  label: string;
  children: React.ReactNode;
}

export default function PeriodPopover({
  open,
  onClose,
  anchorRef,
  label,
  children,
}: PeriodPopoverProps): React.ReactElement | null {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState({ up: false, right: false });

  useLayoutEffect(() => {
    if (!open) return undefined;

    const measure = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      setPlacement({
        up: rect.bottom + ESTIMATED_HEIGHT > window.innerHeight,
        right: rect.left + ESTIMATED_WIDTH > window.innerWidth,
      });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
      anchorRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={label}
      className={`dr-popover${placement.up ? ' dr-popover--up' : ''}${
        placement.right ? ' dr-popover--right' : ''
      }`}
    >
      {children}
    </div>
  );
}
